import React, { useEffect, useState } from "react";
import {
  Table,
  Card,
  Form,
  Input,
  Select,
  Button,
  Space,
  Tag,
  message,
  Modal,
  InputNumber,
  DatePicker,
} from "antd";
import { SearchOutlined, ReloadOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import { TokenService } from "../../services";
import { TokenRecord, TokenOperationDto } from "../../types/token";
import ErrorHandler from "../../utils/errorHandler";
import MainLayout from "../../components/layout/MainLayout";
import "./TokenRecords.scss";

const { Option } = Select;
const { RangePicker } = DatePicker;

const TokenRecordsPage: React.FC = () => {
  const [records, setRecords] = useState<TokenRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [operationModalVisible, setOperationModalVisible] = useState(false);
  const [searchForm] = Form.useForm();
  const [operationForm] = Form.useForm();

  const [searchAccountId, setSearchAccountId] = useState<number>();
  const [searchPhoneNumber, setSearchPhoneNumber] = useState<string>();
  const [searchOperationType, setSearchOperationType] = useState<string>();
  const [searchDateRange, setSearchDateRange] = useState<[Dayjs, Dayjs] | null>(
    null
  );
  const [currentOperationAccountId, setCurrentOperationAccountId] =
    useState<number>();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });

  // 加载用户Token记录
  const loadUserRecords = async (
    accountId?: number,
    phoneNumber?: string,
    operationType?: string,
    startDate?: string,
    endDate?: string,
    page: number = 1,
    pageSize: number = 20
  ) => {
    setLoading(true);
    try {
      const response = await TokenService.getUserTokenLogs(
        accountId,
        phoneNumber,
        operationType,
        startDate,
        endDate,
        page,
        pageSize
      );

      if (response.code === 0 && response.data) {
        const data = response.data;
        setRecords(data.list || []);
        setPagination((prev) => ({
          ...prev,
          current: data.page || 1,
          pageSize: data.limit || 20,
          total: data.total || 0,
        }));
      } else {
        message.error(response.msg || "获取Token记录失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 搜索Token记录
  const handleSearch = async (values: any) => {
    const { accountId, phoneNumber, operationType, dateRange } = values;

    if (!accountId && !phoneNumber) {
      message.warning("请输入账号ID或手机号");
      return;
    }

    // 处理时间范围
    let startDate: string | undefined;
    let endDate: string | undefined;
    if (dateRange && dateRange.length === 2) {
      startDate = dateRange[0].format("YYYY-MM-DD");
      endDate = dateRange[1].format("YYYY-MM-DD");
    }

    setSearchAccountId(accountId);
    setSearchPhoneNumber(phoneNumber);
    setSearchOperationType(operationType);
    setSearchDateRange(dateRange || null);

    await loadUserRecords(
      accountId,
      phoneNumber,
      operationType,
      startDate,
      endDate
    );
  };

  useEffect(() => {
    loadUserRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    setSearchAccountId(undefined);
    setSearchPhoneNumber(undefined);
    setSearchOperationType(undefined);
    setSearchDateRange(null);
    setRecords([]);
    setPagination((prev) => ({ ...prev, current: 1 }));
    loadUserRecords();
  };

  // 打开Token操作弹窗
  const handleOpenOperation = (accountId: number) => {
    setCurrentOperationAccountId(accountId);
    setOperationModalVisible(true);
    operationForm.resetFields();
  };

  // Token操作
  const handleOperation = async (values: TokenOperationDto) => {
    if (!currentOperationAccountId) {
      message.error("请选择要操作的账号");
      return;
    }

    try {
      setLoading(true);
      const response = await TokenService.performOperationForUser(
        currentOperationAccountId,
        { ...values, accountId: currentOperationAccountId }
      );

      if (response.code === 0) {
        message.success("Token操作成功");
        setOperationModalVisible(false);
        operationForm.resetFields();
        setCurrentOperationAccountId(undefined);
        // 重新加载当前页面的记录
        const startDate = searchDateRange?.[0]?.format("YYYY-MM-DD");
        const endDate = searchDateRange?.[1]?.format("YYYY-MM-DD");
        loadUserRecords(
          searchAccountId,
          searchPhoneNumber,
          searchOperationType,
          startDate,
          endDate,
          pagination.current,
          pagination.pageSize
        );
      } else {
        message.error(response.msg || "操作失败");
      }
    } catch (error) {
      ErrorHandler.handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  // 渲染操作类型
  const renderOperationType = (type: string) => {
    const typeMap: Record<string, { text: string; color: string }> = {
      register_gift: { text: "注册赠送", color: "green" },
      recharge: { text: "充值", color: "blue" },
      consume: { text: "消耗", color: "red" },
      activity_gift: { text: "活动赠送", color: "purple" },
    };

    const config = typeMap[type] || { text: type, color: "default" };
    return <Tag color={config.color}>{config.text}</Tag>;
  };

  // 渲染金额变动
  const renderAmount = (amount: number) => {
    const isPositive = amount > 0;
    return (
      <span style={{ color: isPositive ? "#52c41a" : "#ff4d4f" }}>
        {isPositive ? "+" : ""}
        {amount}
      </span>
    );
  };

  // 表格列定义
  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
    },
    {
      title: "账号ID",
      dataIndex: "accountId",
      key: "accountId",
      width: 80,
    },
    {
      title: "手机号",
      key: "account.phone",
      width: 120,
      render: (text: any, record: TokenRecord) => record.account?.phone || "-",
    },
    {
      title: "用户名",
      key: "account.username",
      width: 120,
      render: (text: any, record: TokenRecord) =>
        record.account?.username || "-",
    },
    {
      title: "操作类型",
      dataIndex: "operationType",
      key: "operationType",
      width: 100,
      render: renderOperationType,
    },
    {
      title: "操作数量",
      dataIndex: "operationAmount",
      key: "operationAmount",
      width: 100,
      render: renderAmount,
    },
    {
      title: "操作前余额",
      dataIndex: "balanceBefore",
      key: "balanceBefore",
      width: 100,
      render: (balance: number) => balance,
    },
    {
      title: "操作后余额",
      dataIndex: "balanceAfter",
      key: "balanceAfter",
      width: 100,
      render: (balance: number) => balance,
    },
    {
      title: "任务ID",
      dataIndex: "taskId",
      key: "taskId",
      width: 80,
      render: (taskId: number) => taskId || "-",
    },
    {
      title: "备注",
      dataIndex: "remark",
      key: "remark",
      width: 200,
      ellipsis: true,
      render: (remark: string) => remark || "-",
    },
    {
      title: "操作时间",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 180,
    },
    {
      title: "操作",
      key: "action",
      width: 100,
      render: (text: any, record: TokenRecord) => (
        <Button
          type="primary"
          size="small"
          onClick={() => handleOpenOperation(record.accountId)}
        >
          Token操作
        </Button>
      ),
    },
  ];

  return (
    <MainLayout title="Token记录">
      <div className="token-records">
        {/* 搜索区域 */}
        <Card className="search-card" style={{ marginBottom: 16 }}>
          <Form form={searchForm} layout="inline" onFinish={handleSearch}>
            <Form.Item name="accountId" label="账号ID">
              <InputNumber
                placeholder="请输入账号ID"
                style={{ width: 150 }}
                min={1}
              />
            </Form.Item>
            <Form.Item name="phoneNumber" label="手机号">
              <Input placeholder="请输入手机号" style={{ width: 150 }} />
            </Form.Item>
            {/* <Form.Item name="operationType" label="操作类型">
              <Select
                placeholder="请选择操作类型"
                style={{ width: 150 }}
                allowClear
              >
                <Option value="register_gift">注册赠送</Option>
                <Option value="recharge">充值</Option>
                <Option value="consume">消耗</Option>
                <Option value="activity_gift">活动赠送</Option>
              </Select>
            </Form.Item>
            <Form.Item name="dateRange" label="时间周期">
              <RangePicker
                style={{ width: 300 }}
                format="YYYY-MM-DD"
                placeholder={["开始日期", "结束日期"]}
              />
            </Form.Item> */}

            <Form.Item>
              <Space>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SearchOutlined />}
                >
                  查询记录
                </Button>
                <Button onClick={handleReset} icon={<ReloadOutlined />}>
                  重置
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Card>

        {/* Token记录列表 */}
        <Card title="Token操作记录" className="records-table-card">
          <Table
            columns={columns}
            dataSource={records}
            rowKey="id"
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) =>
                `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              onChange: (page, pageSize) => {
                setPagination((prev) => ({
                  ...prev,
                  current: page,
                  pageSize: pageSize || 20,
                }));
                const startDate = searchDateRange?.[0]?.format("YYYY-MM-DD");
                const endDate = searchDateRange?.[1]?.format("YYYY-MM-DD");
                loadUserRecords(
                  searchAccountId,
                  searchPhoneNumber,
                  searchOperationType,
                  startDate,
                  endDate,
                  page,
                  pageSize || 20
                );
              },
            }}
            scroll={{ x: 1200 }}
          />
        </Card>

        {/* Token操作弹窗 */}
        <Modal
          title={`Token操作 - 账号ID: ${currentOperationAccountId || ""}`}
          open={operationModalVisible}
          onOk={() => operationForm.submit()}
          onCancel={() => {
            setOperationModalVisible(false);
            setCurrentOperationAccountId(undefined);
          }}
          confirmLoading={loading}
          width={500}
        >
          <Form
            form={operationForm}
            layout="vertical"
            onFinish={handleOperation}
          >
            <Form.Item
              name="operationType"
              label="操作类型"
              rules={[{ required: true, message: "请选择操作类型" }]}
            >
              <Select placeholder="请选择操作类型">
                <Option value="recharge">充值</Option>
                <Option value="consume">消耗</Option>
                <Option value="activity_gift">活动赠送</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="operationAmount"
              label="操作数量"
              rules={[
                { required: true, message: "请输入操作数量" },
                {
                  type: "number",
                  min: 100,
                  message: "操作数量必须大于等于100",
                },
                {
                  validator: (_, value) => {
                    if (value == null || value === "") {
                      return Promise.resolve();
                    }
                    if (value % 100 !== 0) {
                      return Promise.reject(
                        new Error("操作数量必须是100的倍数")
                      );
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <InputNumber
                placeholder="请输入Token数量（必须为100的倍数）"
                style={{ width: "100%" }}
                min={100}
                step={100}
                precision={0}
              />
            </Form.Item>

            <Form.Item name="taskId" label="任务ID">
              <InputNumber
                placeholder="请输入任务ID（可选）"
                style={{ width: "100%" }}
                min={1}
                precision={0}
              />
            </Form.Item>

            <Form.Item name="remark" label="备注">
              <Input.TextArea placeholder="请输入操作备注（可选）" rows={3} />
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default TokenRecordsPage;
